import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import type { EsnIconName } from '../../../shared/icons';
import type { EsnDialogTone, EsnOverlayCloseReason } from '../../../shared/types/data';
import { ButtonComponent } from '../../atoms/button/button.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import { TextFieldComponent } from '../../molecules/text-field/text-field.component';

let nextId = 0;

/**
 * Asks the user to confirm one consequential action (archive, deactivate,
 * delete): names the target, offers Cancel beside the single confirm button,
 * and can demand the target's exact name or slug be typed before it arms.
 */
@Component({
  selector: 'esn-dialog',
  imports: [ButtonComponent, IconComponent, TextFieldComponent],
  templateUrl: './dialog.component.html',
  styleUrl: './dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogComponent {
  readonly open = model(false);
  readonly title = input.required<string>();

  /**
   * Explanation of the consequence; richer content can be projected instead.
   */
  readonly body = input('');
  readonly tone = input<EsnDialogTone>('danger');
  readonly icon = input<EsnIconName>('info');
  readonly confirmLabel = input('Confirm');
  readonly cancelLabel = input('Cancel');

  /**
   * When set, the confirm button only arms once exactly this text (e.g. the island slug) is typed.
   */
  readonly confirmText = input<string | null>(null);
  readonly confirmFieldLabel = input('');
  readonly confirmHint = input('');

  /**
   * Render in the page flow without a backdrop — for previews and documentation.
   */
  readonly inline = input(false);

  readonly confirm = output<void>();
  readonly cancel = output<EsnOverlayCloseReason>();

  protected readonly titleId = `esn-dialog-${nextId++}-title`;
  protected readonly bodyId = `${this.titleId}-body`;
  protected readonly typed = signal('');

  protected readonly armed = computed(() => {
    const expected = this.confirmText();
    return expected == null || this.typed() === expected;
  });

  protected readonly confirmVariant = computed(() => {
    if (!this.armed()) return 'secondary';
    return this.tone() === 'danger' ? 'danger-outline' : 'primary';
  });

  private readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

  constructor() {
    effect(() => {
      const el = this.dialog().nativeElement;
      if (this.inline()) {
        el.setAttribute('open', '');
        return;
      }
      const isOpen = el.hasAttribute('open');
      if (this.open() && !isOpen) {
        untracked(() => this.typed.set(''));
        if (typeof el.showModal === 'function') el.showModal();
        else el.setAttribute('open', '');
      } else if (!this.open() && isOpen) {
        if (typeof el.close === 'function') el.close();
        else el.removeAttribute('open');
      }
    });
  }

  /**
   * Confirms the action — only possible once the typed confirmation matches.
   */
  protected handleConfirm(): void {
    if (!this.armed()) return;
    this.confirm.emit();
    if (!this.inline()) this.open.set(false);
  }

  /**
   * Backs out without doing anything.
   */
  protected dismiss(reason: EsnOverlayCloseReason): void {
    this.cancel.emit(reason);
    if (!this.inline()) this.open.set(false);
  }

  protected handleCancel(event: Event): void {
    event.preventDefault();
    this.dismiss('escape');
  }

  protected handleClick(event: MouseEvent): void {
    if (event.target === this.dialog().nativeElement) this.dismiss('backdrop');
  }
}
